import Card from "@/design-system/Card/Card";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
} from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

import { PlusPlus } from "../../../../molecules/PlusPlus/PlusPlus";

type Props = {
  // Empty slots have no name
  name?: string;
  playerRoleId: number;
  rolesPlus?: number[];
  focus?: string;
  position: string;
  isActive?: boolean;
  onClick?: () => void;
};

// EA's Player Roles tile (name, role, focus, position) on a design-system Card
export const PitchRoleTile = ({
  name,
  playerRoleId,
  rolesPlus,
  focus,
  position,
  isActive,
  onClick,
}: Props) => {
  const { t } = useTranslation("squadBuilder");
  const roleName = useSelector(
    playerRoleLabelSelector({ playerRoleId, appendPosition: false }),
  );
  const { positions, focuses } = useSelector(contentLabelsSelector);
  const plusOrPlusPlus = getRolesPlusOrPlusPlus({ playerRoleId, rolesPlus });

  return (
    <div
      className="cursor-pointer"
      onClick={(e) => {
        // The pitch's own click clears the selection on touch devices
        e.stopPropagation();
        onClick?.();
      }}
    >
      <Card
        hoverable={!isActive}
        className={cn(
          "flex flex-col w-24 large-desktop:w-28 px-1.5 py-2.5 text-center text-foreground rounded-xl",
          // Phones fit a back four across 375px
          "max-mobile:w-[72px] max-mobile:px-1 max-mobile:py-2",
          isActive && "bg-surface-raised border-primary-border",
        )}
      >
        <p
          className={cn(
            "truncate text-xs large-desktop:text-sm",
            name ? "font-semibold" : "text-foreground-muted",
          )}
        >
          {name || t("pitch.emptySlot")}
        </p>
        <hr className="my-1.5 max-mobile:my-1 border-edge-strong" />
        <div className="flex flex-wrap items-center justify-center gap-x-1 min-h-4 text-xxs large-desktop:text-xs text-foreground-body">
          {/* Two lines on phones rather than cutting the role off */}
          <span className="truncate max-w-full max-mobile:whitespace-normal max-mobile:leading-3">
            {roleName}
          </span>
          <PlusPlus
            plus={plusOrPlusPlus === "plus"}
            plusPlus={plusOrPlusPlus === "plusPlus"}
          />
        </div>
        <p className="truncate min-h-4 mt-0.5 text-xxs large-desktop:text-xs text-foreground-muted">
          {focuses[focus]?.name ?? focus}
        </p>
        <hr className="my-1.5 max-mobile:my-1 border-edge-strong" />
        <p className="text-sm large-desktop:text-base font-medium">
          {positions[position]?.name ?? position}
        </p>
      </Card>
    </div>
  );
};
